/** Connects the export workspace's selected format and availability to the shared app header. */

import { useEffect } from 'react';
import type { ExportMode } from '../utils/boqExportPreviewUtils';

export const BOQ_EXPORT_HEADER_EVENTS = {
  stateChange: 'boq-export:state-change',
  requestState: 'boq-export:request-state',
  download: 'boq-export:download',
} as const;

export interface BOQExportHeaderState {
  canExport: boolean;
  isExporting: boolean;
}

interface BOQExportHeaderBridgeInput extends BOQExportHeaderState {
  selected: ExportMode;
  onExport: (mode: ExportMode) => void;
}

export function useBOQExportHeaderBridge({
  selected,
  canExport,
  isExporting,
  onExport,
}: BOQExportHeaderBridgeInput): void {
  useEffect(() => {
    const publish = () => window.dispatchEvent(new CustomEvent(BOQ_EXPORT_HEADER_EVENTS.stateChange, {
      detail: { canExport, isExporting } satisfies BOQExportHeaderState,
    }));
    // Either the lazily loaded workspace or the header can mount first.
    window.addEventListener(BOQ_EXPORT_HEADER_EVENTS.requestState, publish);
    publish();
    return () => window.removeEventListener(BOQ_EXPORT_HEADER_EVENTS.requestState, publish);
  }, [canExport, isExporting]);

  useEffect(() => {
    const download = () => {
      if (canExport && !isExporting) onExport(selected);
    };
    window.addEventListener(BOQ_EXPORT_HEADER_EVENTS.download, download);
    return () => window.removeEventListener(BOQ_EXPORT_HEADER_EVENTS.download, download);
  }, [canExport, isExporting, onExport, selected]);

  useEffect(() => () => {
    window.dispatchEvent(new CustomEvent(BOQ_EXPORT_HEADER_EVENTS.stateChange, { detail: null }));
  }, []);
}
